import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Plus, Edit, Trash2, Eye, FileText } from 'lucide-react';
import { toast } from 'react-hot-toast';
import apiService from '../../services/api';
import DataTable from '../common/DataTable';
import Modal from '../common/Modal';

const Blogs = ({ user }) => {
  const [blogs, setBlogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [editingBlog, setEditingBlog] = useState(null);
  const [selectedBlog, setSelectedBlog] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    content: '',
    excerpt: '',
    category: '',
    tags: '',
    status: 'draft',
    featured_image: null
  });

  useEffect(() => {
    fetchBlogs();
  }, []);

  const fetchBlogs = async () => {
    try {
      const token = localStorage.getItem('token');
      if (token) {
        apiService.setToken(token);
      }
      const blogs = await apiService.getBlogs();
      setBlogs(blogs);
    } catch (error) {
      toast.error('Failed to fetch blogs');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      if (token) {
        apiService.setToken(token);
      }

      const data = new FormData();
      Object.keys(formData).forEach(key => {
        if (formData[key]) data.append(key, formData[key]);
      });

      if (editingBlog) {
        await apiService.updateBlog(editingBlog.id, data);
      } else {
        await apiService.createBlog(data);
      }

      toast.success(`Blog ${editingBlog ? 'updated' : 'created'} successfully`);
      setShowModal(false);
      setEditingBlog(null);
      setFormData({ title: '', content: '', excerpt: '', category: '', tags: '', status: 'draft', featured_image: null });
      fetchBlogs();
    } catch (error) {
      toast.error('Operation failed');
    }
  };

  const handleView = (blog) => {
    setSelectedBlog(blog);
    setShowViewModal(true);
  };

  const handleEdit = (blog) => {
    setEditingBlog(blog);
    setFormData({
      title: blog.title,
      content: blog.content,
      excerpt: blog.excerpt,
      category: blog.category,
      tags: blog.tags ? blog.tags.join(', ') : '',
      status: blog.status,
      featured_image: null
    });
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    toast((t) => (
      <div>
        <p>Are you sure you want to delete this blog?</p>
        <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.5rem' }}>
          <button
            onClick={async () => {
              toast.dismiss(t.id);
              try {
                const token = localStorage.getItem('token');
                if (token) {
                  apiService.setToken(token);
                }
                await apiService.deleteBlog(id);
                toast.success('Blog deleted successfully');
                fetchBlogs();
              } catch (error) {
                toast.error('Failed to delete blog');
              }
            }}
            style={{ background: '#ef4444', color: 'white', border: 'none', padding: '0.25rem 0.5rem', borderRadius: '4px' }}
          >
            Delete
          </button>
          <button
            onClick={() => toast.dismiss(t.id)}
            style={{ background: '#6b7280', color: 'white', border: 'none', padding: '0.25rem 0.5rem', borderRadius: '4px' }}
          >
            Cancel
          </button>
        </div>
      </div>
    ), { duration: Infinity });
  };

  const columns = [
    { key: 'title', label: 'Title' },
    { key: 'category', label: 'Category' },
    {
      key: 'status',
      label: 'Status',
      render: (blog) => blog.status
    },
    { key: 'Author.name', label: 'Author', render: (blog) => blog.Author?.name || 'N/A' },
    {
      key: 'created_at',
      label: 'Created',
      render: (blog) => new Date(blog.created_at).toLocaleDateString()
    },
    {
      key: 'actions',
      label: 'Actions',
      render: (blog) => (
        <div className="action-buttons">
          <button onClick={() => handleView(blog)} className="btn-sm" title="View">
            <Eye size={16} />
          </button>
          <button onClick={() => handleEdit(blog)} className="btn-edit" title="Edit">
            <Edit size={16} />
          </button>
          {user?.role === 'super_admin' && (
            <button onClick={() => handleDelete(blog.id)} className="btn-delete" title="Delete">
              <Trash2 size={16} />
            </button>
          )}
        </div>
      )
    }
  ];

  return (
    <div className="blog-management">
      <div className="page-header">
        <h2><FileText size={24} /> Blog Management</h2>
        <motion.button
          className="btn-primary"
          onClick={() => setShowModal(true)}
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
        >
          <Plus size={16} /> Add Blog Post
        </motion.button>
      </div>

      <DataTable
        data={blogs}
        columns={columns}
        loading={loading}
        emptyMessage="No blogs found"
      />

      <Modal
        isOpen={showModal}
        onClose={() => {
          setShowModal(false);
          setEditingBlog(null);
          setFormData({ title: '', content: '', excerpt: '', category: '', tags: '', status: 'draft', featured_image: null });
        }}
        title={`${editingBlog ? 'Edit' : 'Add'} Blog Post`}
        size="lg"
      >
        <form onSubmit={handleSubmit} style={{ textAlign: 'left' }}>
          <div className="form-group">
            <label>Title</label>
            <input
              type="text"
              required
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
            />
          </div>
          <div className="form-group">
            <label>Excerpt</label>
            <textarea
              value={formData.excerpt}
              onChange={(e) => setFormData({ ...formData, excerpt: e.target.value })}
              rows="3"
            />
          </div>
          <div className="form-group">
            <label>Content</label>
            <textarea
              required
              value={formData.content}
              onChange={(e) => setFormData({ ...formData, content: e.target.value })}
              rows="6"
            />
          </div>
          <div className="form-row">
            <div className="form-group">
              <label>Category</label>
              <input
                type="text"
                value={formData.category}
                onChange={(e) => setFormData({ ...formData, category: e.target.value })}
              />
            </div>
            <div className="form-group">
              <label>Tags</label>
              <input
                type="text"
                placeholder="Comma separated"
                value={formData.tags}
                onChange={(e) => setFormData({ ...formData, tags: e.target.value })}
              />
            </div>
          </div>
          <div className="form-row">
            <div className="form-group">
              <label>Status</label>
              <select
                value={formData.status}
                onChange={(e) => setFormData({ ...formData, status: e.target.value })}
              >
                <option value="draft">Draft</option>
                <option value="published">Published</option>
                <option value="archived">Archived</option>
              </select>
            </div>
            <div className="form-group">
              <label>Featured Image</label>
              <input
                type="file"
                accept="image/*"
                onChange={(e) => setFormData({ ...formData, featured_image: e.target.files[0] })}
              />
            </div>
          </div>

          <div className="modal-actions">
            <button
              type="button"
              className="btn-secondary"
              onClick={() => {
                setShowModal(false);
                setEditingBlog(null);
                setFormData({ title: '', content: '', excerpt: '', category: '', tags: '', status: 'draft', featured_image: null });
              }}
            >
              Cancel
            </button>
            <button type="submit" className="btn-primary">
              {editingBlog ? 'Update' : 'Create'}
            </button>
          </div>
        </form>
      </Modal>

      {/* View Modal */}
      <Modal
        isOpen={showViewModal}
        onClose={() => setShowViewModal(false)}
        title="Blog Details"
        size="lg"
      >
        {selectedBlog && (
          <div style={{ textAlign: 'left', padding: '0' }}>
            {/* Header Section */}
            <div style={{
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              padding: '2rem',
              borderRadius: '12px 12px 0 0',
              marginBottom: '1.5rem',
              color: 'white',
              textAlign: 'center'
            }}>
              <div style={{
                width: '80px',
                height: '80px',
                borderRadius: '50%',
                background: 'rgba(255,255,255,0.2)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto 1rem',
                fontSize: '2rem',
                fontWeight: 'bold'
              }}>
                <FileText size={32} />
              </div>
              <h3 style={{ margin: '0 0 0.5rem', fontSize: '1.5rem' }}>{selectedBlog.title}</h3>
              <p style={{ margin: '0', opacity: '0.9' }}>{selectedBlog.category || 'Blog Post'}</p>
            </div>

            {/* Content Info */}
            <div style={{ marginBottom: '1.5rem' }}>
              <h4 style={{ margin: '0 0 1rem', color: '#374151', fontSize: '1.1rem' }}>Content</h4>
              <div style={{ display: 'grid', gap: '1rem' }}>
                {selectedBlog.excerpt && (
                  <div style={{ padding: '0.75rem', background: '#f9fafb', borderRadius: '8px' }}>
                    <strong style={{ color: '#374151' }}>Excerpt:</strong>
                    <p style={{ margin: '0.5rem 0 0', color: '#6b7280' }}>{selectedBlog.excerpt}</p>
                  </div>
                )}
                <div style={{ padding: '0.75rem', background: '#f9fafb', borderRadius: '8px' }}>
                  <strong style={{ color: '#374151' }}>Content:</strong>
                  <div style={{ margin: '0.5rem 0 0', color: '#6b7280', maxHeight: '200px', overflow: 'auto' }}>
                    {selectedBlog.content}
                  </div>
                </div>
              </div>
            </div>

            {/* Meta Information */}
            <div style={{ marginBottom: '1.5rem' }}>
              <h4 style={{ margin: '0 0 1rem', color: '#374151', fontSize: '1.1rem' }}>Details</h4>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                <div style={{ padding: '1rem', background: '#f3f4f6', borderRadius: '8px' }}>
                  <div style={{ fontSize: '0.875rem', color: '#6b7280', marginBottom: '0.25rem' }}>Author</div>
                  <div style={{ fontWeight: '500', color: '#374151' }}>{selectedBlog.Author?.name || 'N/A'}</div>
                </div>
                <div style={{ padding: '1rem', background: '#f3f4f6', borderRadius: '8px' }}>
                  <div style={{ fontSize: '0.875rem', color: '#6b7280', marginBottom: '0.25rem' }}>Created</div>
                  <div style={{ fontWeight: '500', color: '#374151' }}>{new Date(selectedBlog.created_at).toLocaleDateString()}</div>
                </div>
                {selectedBlog.tags && (
                  <div style={{ padding: '1rem', background: '#f3f4f6', borderRadius: '8px', gridColumn: '1 / -1' }}>
                    <div style={{ fontSize: '0.875rem', color: '#6b7280', marginBottom: '0.25rem' }}>Tags</div>
                    <div style={{ fontWeight: '500', color: '#374151' }}>{Array.isArray(selectedBlog.tags) ? selectedBlog.tags.join(', ') : selectedBlog.tags}</div>
                  </div>
                )}
              </div>
            </div>

            {/* Status Badge */}
            <div style={{ textAlign: 'center' }}>
              <span style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '0.5rem',
                padding: '0.5rem 1rem',
                background: selectedBlog.status === 'published' ? '#dcfce7' : selectedBlog.status === 'draft' ? '#fef3c7' : '#f3f4f6',
                color: selectedBlog.status === 'published' ? '#166534' : selectedBlog.status === 'draft' ? '#92400e' : '#374151',
                borderRadius: '20px',
                fontSize: '0.875rem',
                fontWeight: '500'
              }}>
                <div style={{
                  width: '8px',
                  height: '8px',
                  background: selectedBlog.status === 'published' ? '#22c55e' : selectedBlog.status === 'draft' ? '#f59e0b' : '#6b7280',
                  borderRadius: '50%'
                }} />
                {selectedBlog.status}
              </span>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default Blogs;